import React, { useCallback, useEffect, useRef, useState } from 'react';
import { pagamentosApi } from '../api/api';
import { formatCpf, isValidCpf, normalizeCpf } from '../utils/cpf';

const VALOR_MATURADOR = 49.90;

function loadMpSdk(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any).MercadoPago) { resolve(); return; }
    const existing = document.getElementById('mp-sdk-script');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.id = 'mp-sdk-script';
    s.src = 'https://sdk.mercadopago.com/js/v2';
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

function MaturadorCardBrick({
  onApproved, onError,
}: {
  onApproved: () => void;
  onError: (msg: string) => void;
}) {
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState(false);
  const brickControllerRef = useRef<any>(null);
  const containerId = 'mp-card-brick-maturador';

  // Refs estaveis para callbacks — evita remontar o brick a cada render do pai
  const onApprovedRef = useRef(onApproved);
  const onErrorRef = useRef(onError);
  useEffect(() => { onApprovedRef.current = onApproved; }, [onApproved]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await pagamentosApi.getPublicKey();
        if (cancelled) return;
        if (!data.publicKey) { setSdkError(true); return; }
        await loadMpSdk();
        if (cancelled) return;

        const mp = new (window as any).MercadoPago(data.publicKey, { locale: 'pt-BR' });
        const bricksBuilder = mp.bricks();
        const controller = await bricksBuilder.create('cardPayment', containerId, {
          initialization: {
            amount: VALOR_MATURADOR,
            payer: { email: '' },
          },
          customization: {
            visual: {
              style: {
                theme: 'flat',
                customVariables: {
                  baseColor: '#3B82F6',
                  baseColorFirstVariant: '#2563EB',
                  baseColorSecondVariant: '#DBEAFE',
                  outlinePrimaryColor: '#3B82F6',
                  buttonBackground: '#3B82F6',
                  buttonHoverBackground: '#2563EB',
                  inputFocusedLabelColor: '#FFFFFF',
                  inputFocusedBorderColor: '#3B82F6',
                  inputBorderColor: '#E2E7F1',
                  errorColor: '#EF4444',
                  fontFamilyBase: 'Fustat, system-ui, sans-serif',
                  fontSizeMedium: '14px',
                  fontSizeSmall: '11px',
                  fontWeightNormal: '400',
                  fontWeightSemiBold: '600',
                  borderRadiusMedium: '8px',
                  borderRadiusSmall: '6px',
                  borderRadiusFull: '8px',
                  formBackgroundColor: '#FFFFFF',
                  inputBackgroundColor: '#FFFFFF',
                  inputPlaceholderColor: '#8A8AA3',
                  inputFontSize: '14px',
                  labelFontSize: '11px',
                  labelFontWeight: '600',
                  labelColor: '#56606D',
                },
              },
              hideFormTitle: true,
              defaultPaymentOption: { creditCardForm: true },
            },
            paymentMethods: { minInstallments: 1, maxInstallments: 1 },
          },
          callbacks: {
            onReady: () => { if (!cancelled) setSdkReady(true); },
            onSubmit: async (formData: any) => {
              const { data: result } = await pagamentosApi.criarCartaoDireto({
                token: formData.token,
                payment_method_id: formData.payment_method_id,
                issuer_id: formData.issuer_id,
                installments: formData.installments ?? 1,
                payer: formData.payer,
                addon: 'maturador',
              });
              if (result.status === 'approved' || result.statusInterno === 'in_process') {
                onApprovedRef.current();
              } else {
                throw new Error(result.mensagemAmigavel || 'Pagamento não aprovado.');
              }
            },
            onError: (err: any) => onErrorRef.current(err?.message || 'Erro no formulário de cartão.'),
          },
        });

        if (cancelled) controller.unmount();
        else brickControllerRef.current = controller;
      } catch {
        if (!cancelled) setSdkError(true);
      }
    })();
    return () => {
      cancelled = true;
      brickControllerRef.current?.unmount();
      brickControllerRef.current = null;
    };
  }, []);

  if (sdkError) {
    return (
      <div className="text-center text-sm text-red-600 py-6">
        Não foi possível carregar o formulário de cartão. Tente novamente em instantes ou use PIX.
      </div>
    );
  }

  return (
    <div>
      {!sdkReady && (
        <div className="flex items-center justify-center py-8 gap-2 text-sm text-ink-secondary">
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Carregando formulário...
        </div>
      )}
      <div id={containerId} />
    </div>
  );
}

interface Props {
  onApproved: () => void;
  onVoltar: () => void;
  cpfDoUsuario?: string | null;
}

type Etapa = 'metodo' | 'qrcode' | 'aprovado' | 'erro';

export default function MaturadorCheckoutPage({ onApproved, onVoltar, cpfDoUsuario }: Props) {
  const [etapa, setEtapa] = useState<Etapa>('metodo');
  const [metodo, setMetodo] = useState<'pix' | 'cartao'>('pix');
  const [cpf, setCpf] = useState(cpfDoUsuario ? formatCpf(cpfDoUsuario) : '');
  const [gerando, setGerando] = useState(false);
  const [pix, setPix] = useState<{ paymentId: string; qrCode: string; qrCodeBase64: string } | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [erro, setErro] = useState('');
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  const gerarPix = useCallback(async (cpfFinal: string) => {
    setGerando(true);
    setErro('');
    try {
      const { data } = await pagamentosApi.criarPixMaturador(cpfFinal);
      setPix({ paymentId: data.paymentId, qrCode: data.qrCode, qrCodeBase64: data.qrCodeBase64 });
      setEtapa(data.status === 'approved' ? 'aprovado' : 'qrcode');
    } catch (err: any) {
      setErro(err?.response?.data?.message || 'Erro ao gerar PIX');
      setEtapa('erro');
    } finally {
      setGerando(false);
    }
  }, []);

  useEffect(() => {
    if (etapa !== 'qrcode' || !pix?.paymentId) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await pagamentosApi.consultarStatus(pix.paymentId);
        if (data.status === 'approved') {
          if (pollRef.current) clearInterval(pollRef.current);
          setEtapa('aprovado');
          setTimeout(() => onApproved(), 1500);
        } else if (['rejected', 'cancelled', 'refunded', 'charged_back'].includes(data.status)) {
          if (pollRef.current) clearInterval(pollRef.current);
          setErro(data.mensagemAmigavel || 'Pagamento rejeitado');
          setEtapa('erro');
        }
      } catch {}
    }, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [etapa, pix, onApproved]);

  const handleFinalizar = (e: React.FormEvent) => {
    e.preventDefault();
    const norm = normalizeCpf(cpf);
    if (!norm) { setErro('Informe seu CPF'); return; }
    if (!isValidCpf(norm)) { setErro('CPF inválido'); return; }
    gerarPix(norm);
  };

  const copiarCodigo = () => {
    if (!pix?.qrCode) return;
    navigator.clipboard.writeText(pix.qrCode);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2500);
  };

  return (
    <div className="max-w-5xl mx-auto w-full space-y-5">

      {/* Link voltar — alto à direita */}
      <div className="flex justify-end">
        <button
          onClick={onVoltar}
          className="flex items-center gap-1.5 text-sm text-ink-secondary hover:text-ink-primary transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Voltar
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5 items-start">

        {/* ── COLUNA ESQUERDA — método de pagamento ── */}
        <div className="bg-white border border-line rounded-card p-6 space-y-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-secondary">
            Escolha um método de pagamento
          </p>

          {/* PIX */}
          <div
            className="rounded-card border border-line transition-colors"
            style={metodo === 'pix' ? { background: '#FAFAFA' } : undefined}
          >
            <button
              type="button"
              onClick={() => setMetodo('pix')}
              className="w-full flex items-center gap-3 px-5 py-4"
            >
              <span
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors`}
                style={{ borderColor: metodo === 'pix' ? '#3B82F6' : '#D1D5DB' }}
              >
                {metodo === 'pix' && (
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#3B82F6' }} />
                )}
              </span>
              <span className="text-sm font-semibold text-ink-primary">PIX</span>
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(37, 99, 235, 0.18)', color: '#3B82F6' }}
              >
                Recomendado
              </span>
              <span className="ml-auto text-xs text-ink-secondary font-semibold tracking-wider">PIX</span>
            </button>

            {metodo === 'pix' && etapa === 'metodo' && (
              <form onSubmit={handleFinalizar} className="px-5 pb-5 space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-ink-secondary mb-1.5">CPF do titular</label>
                  <input
                    type="text"
                    value={cpf}
                    onChange={e => { setCpf(formatCpf(e.target.value)); setErro(''); }}
                    placeholder="000.000.000-00"
                    inputMode="numeric"
                    maxLength={14}
                    className="w-full bg-white border border-line rounded-btn px-3.5 py-3 text-sm text-ink-primary placeholder:text-ink-secondary focus:outline-none focus:border-brand-primary transition-colors"
                  />
                </div>
                {erro && <p className="text-xs text-red-600">{erro}</p>}
                <button
                  type="submit"
                  disabled={gerando}
                  className="w-full h-12 rounded-btn text-sm font-semibold hover:opacity-80 transition-opacity disabled:opacity-50"
                  style={{ background: '#3B82F6', color: '#FFFFFF' }}
                >
                  {gerando ? 'Gerando PIX...' : 'Finalizar pagamento com PIX'}
                </button>
                <p className="text-center text-xs text-ink-secondary">Leva menos de 30 segundos para concluir</p>
              </form>
            )}

            {metodo === 'pix' && etapa === 'qrcode' && pix && (
              <div className="px-5 pb-5 space-y-4">
                {pix.qrCodeBase64 && (
                  <div className="flex flex-col items-center">
                    <div className="p-3 bg-white rounded-card border border-line">
                      <img
                        src={`data:image/png;base64,${pix.qrCodeBase64}`}
                        alt="QR Code PIX"
                        className="w-56 h-56 block"
                      />
                    </div>
                    <p className="text-xs text-ink-secondary mt-3 text-center">
                      Aponte a câmera do app do seu banco para o QR Code
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-ink-secondary mb-1.5">
                    Ou copie o código PIX:
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={pix.qrCode}
                      className="flex-1 bg-surface-subtle border border-line rounded-btn px-3 py-2 text-xs text-ink-primary font-mono"
                    />
                    <button
                      type="button"
                      onClick={copiarCodigo}
                      className="px-4 py-2 rounded-btn text-xs font-semibold hover:opacity-80 transition-opacity flex-shrink-0"
                      style={{ background: '#3B82F6', color: '#FFFFFF' }}
                    >
                      {copiado ? 'Copiado!' : 'Copiar'}
                    </button>
                  </div>
                </div>

                <div className="bg-surface-subtle rounded-btn p-3.5 flex items-center gap-3">
                  <svg className="w-5 h-5 animate-spin text-ink-secondary flex-shrink-0" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <div>
                    <p className="text-sm font-semibold text-ink-primary">Aguardando pagamento</p>
                    <p className="text-xs text-ink-secondary">Liberação automática após confirmação do banco.</p>
                  </div>
                </div>
              </div>
            )}

            {metodo === 'pix' && etapa === 'aprovado' && (
              <div className="px-5 pb-6 flex flex-col items-center text-center gap-2">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mt-3" style={{ background: '#3B82F6' }}>
                  <svg className="w-7 h-7" fill="none" stroke="#FFFFFF" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-base font-bold text-ink-primary">Pagamento aprovado!</p>
                <p className="text-sm text-ink-secondary">Liberando seu Maturador de Chip...</p>
              </div>
            )}

            {metodo === 'pix' && etapa === 'erro' && (
              <div className="px-5 pb-5 space-y-3 text-center">
                <p className="text-sm text-red-600">{erro || 'Não foi possível processar o pagamento.'}</p>
                <button
                  type="button"
                  onClick={() => { setEtapa('metodo'); setPix(null); setErro(''); }}
                  className="px-5 py-2.5 rounded-btn text-sm font-semibold hover:opacity-80 transition-opacity"
                  style={{ background: '#3B82F6', color: '#FFFFFF' }}
                >
                  Tentar novamente
                </button>
              </div>
            )}
          </div>

          {/* Cartão de crédito ou débito */}
          <div
            className="rounded-card border border-line transition-colors"
            style={metodo === 'cartao' ? { background: '#FAFAFA' } : undefined}
          >
            <button
              type="button"
              onClick={() => setMetodo('cartao')}
              className="w-full flex items-center gap-3 px-5 py-4"
            >
              <span
                className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors"
                style={{ borderColor: metodo === 'cartao' ? '#3B82F6' : '#D1D5DB' }}
              >
                {metodo === 'cartao' && (
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#3B82F6' }} />
                )}
              </span>
              <span className="text-sm font-semibold text-ink-primary">Cartão de crédito ou débito</span>
            </button>

            {metodo === 'cartao' && etapa !== 'aprovado' && (
              <div className="px-5 pb-5">
                <MaturadorCardBrick
                  onApproved={() => { setEtapa('aprovado'); setTimeout(() => onApproved(), 1500); }}
                  onError={(msg) => setErro(msg)}
                />
                {erro && <p className="text-xs text-red-600 mt-2">{erro}</p>}
              </div>
            )}

            {metodo === 'cartao' && etapa === 'aprovado' && (
              <div className="px-5 pb-6 flex flex-col items-center text-center gap-2">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mt-3" style={{ background: '#3B82F6' }}>
                  <svg className="w-7 h-7" fill="none" stroke="#FFFFFF" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-base font-bold text-ink-primary">Pagamento aprovado!</p>
                <p className="text-sm text-ink-secondary">Liberando seu Maturador de Chip...</p>
              </div>
            )}
          </div>
        </div>

        {/* ── COLUNA DIREITA — resumo do pedido ── */}
        <div className="bg-white border border-line rounded-card p-6 space-y-5 lg:sticky lg:top-6">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-secondary">
            Resumo do pedido
          </p>

          <div>
            <span className="text-sm text-ink-secondary">Produto: </span>
            <span className="text-sm font-bold text-ink-primary">Maturador de Chip</span>
          </div>

          <div className="pt-4 border-t border-line flex items-baseline justify-between">
            <span className="text-sm text-ink-secondary">Total</span>
            <div className="text-right">
              <span className="text-2xl font-extrabold text-ink-primary tracking-tight">R$ 49,90</span>
              <span className="text-xs text-ink-secondary ml-1">pagamento único</span>
            </div>
          </div>

          <div className="pt-4 border-t border-line space-y-2.5">
            {[
              { d: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z', text: 'Pagamento seguro via Mercado Pago' },
              { d: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z', text: 'Seus dados são criptografados' },
              { d: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', text: 'Liberação após confirmação do pagamento' },
            ].map(({ d, text }) => (
              <div key={text} className="flex items-center gap-2 text-xs text-ink-secondary">
                <svg className="w-3.5 h-3.5 text-ink-secondary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
                </svg>
                {text}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
